import React from 'react';
import { Link } from 'react-router-dom';
import { RecipeCard } from '@/components/recipe/RecipeCard';
import { useRelatedRecipes } from '@/hooks/useRelatedRecipes';
import { useLanguage } from '@/context/LanguageContext';
import { t } from '@/i18n';
import { MapPin, ChevronRight, BookOpen } from 'lucide-react';

const RelatedRecipes = ({ recipe, lang }) => {
  const { data: related, isLoading } = useRelatedRecipes(recipe, lang);
  const { getLocalizedPath } = useLanguage();
  const country = recipe?.origin_country || recipe?.country;
  const countrySlug = country?.toLowerCase().replace(/\s+/g, '-');

  if (isLoading || !related || related.length === 0) return null;

  return (
    <section className="mt-8 sm:mt-12 space-y-6" data-testid="related-recipes-section">
      <div>
        <h2
          className="text-xl sm:text-2xl font-light text-foreground mb-3"
          style={{ fontFamily: 'var(--font-heading)' }}
        >
          {t('recipe.relatedRecipes', lang) || 'Related Recipes'}
        </h2>
        <div className="section-divider mb-6 sm:mb-8" style={{ marginLeft: 0 }}></div>

        <div className="hidden lg:grid grid-cols-4 gap-5" data-testid="related-recipes-grid">
          {related.map((r) => (
            <RecipeCard key={r.slug} recipe={r} variant="editorial" deferFavoriteCheck />
          ))}
        </div>

        <div className="lg:hidden scroll-container -mx-4 px-4" data-testid="related-recipes-scroll">
          {related.map((r) => (
            <div key={r.slug} className="min-w-[260px] max-w-[280px] flex-shrink-0">
              <RecipeCard recipe={r} variant="editorial" deferFavoriteCheck />
            </div>
          ))}
        </div>
      </div>

      {/* Explore more from {Country} */}
      {country && (
        <Link
          to={getLocalizedPath(`/country/${countrySlug}`)}
          className="inline-flex items-center gap-2 text-sm text-primary hover:underline transition-colors"
          data-testid="explore-country-link"
        >
          <MapPin className="h-4 w-4" />
          {t('recipe.exploreMoreFrom', lang) || 'Explore more recipes from'} {country}
          <ChevronRight className="h-3 w-3" />
        </Link>
      )}

      {/* Internal technique links */}
      {recipe?.technique_links?.length > 0 && (
        <Link
          to={getLocalizedPath('/techniques')}
          className="inline-flex items-center gap-2 text-sm text-primary hover:underline transition-colors ml-4"
          data-testid="techniques-link"
        >
          <BookOpen className="h-4 w-4" />
          {t('recipe.learnTechniques', lang) || 'Learn Cooking Techniques'}
          <ChevronRight className="h-3 w-3" />
        </Link>
      )}
    </section>
  );
};

export default RelatedRecipes;
